import React from 'react'
import { Link, type LinkProps } from 'react-router-dom'
import { useRouter } from '~/hooks/useRouter'
import { useLocale, withLocale, LocaleHref } from '~/hooks/useLocale'

type LocalizedLinkProps = Omit<LinkProps, 'to'> & {
  href: LocaleHref
  children?: React.ReactNode
}

function toRouterPath(href: LocaleHref): string {
  if (typeof href === 'string') return href
  const pathname = typeof href.pathname === 'string' ? href.pathname : '/'
  const q = href.query
  if (!q || typeof q !== 'object') return pathname
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(q as Record<string, unknown>)) {
    if (v == null) continue
    if (Array.isArray(v)) v.forEach((vv) => params.append(k, String(vv)))
    else params.append(k, String(v))
  }
  const s = params.toString()
  return s ? `${pathname}?${s}` : pathname
}

export const LocalizedLink: React.FC<LocalizedLinkProps> = ({
  href,
  children,
  ...rest
}) => {
  const locale = useLocale()
  return (
    <Link to={toRouterPath(withLocale(href, locale))} {...rest}>
      {children}
    </Link>
  )
}

export function useLocalizedRouter() {
  const router = useRouter()
  const locale = useLocale()
  return {
    push: (href: LocaleHref) =>
      router.push(withLocale(href, locale) as Parameters<typeof router.push>[0]),
    replace: (href: LocaleHref) =>
      router.replace(withLocale(href, locale) as Parameters<typeof router.replace>[0]),
    locale,
    router,
  }
}
